import { inject } from '@angular/core';
import { CanActivateFn, RedirectFunction, Router } from '@angular/router';
import { AuthService } from './auth.service';
import { safeAuthReturnUrl } from './auth-return-url';
import {
  AuthorizationService,
  PermissionMode,
  PermissionRequirement,
} from './authorization.service';

function permissionRequirement(data: Record<string, unknown>): PermissionRequirement | null {
  const permissions = data['permissions'];
  const mode = data['permissionMode'];

  if (
    !Array.isArray(permissions) ||
    permissions.length === 0 ||
    !permissions.every((permission) =>
      typeof permission === 'string' && permission.length > 0 && permission.trim() === permission
    ) ||
    (mode !== 'all' && mode !== 'any')
  ) {
    return null;
  }

  return {
    permissions,
    mode: mode as PermissionMode,
  };
}

export const permissionGuard: CanActivateFn = async (route, state) => {
  const auth = inject(AuthService);
  const authorization = inject(AuthorizationService);
  const router = inject(Router);

  if (!await auth.restoreSession()) {
    return router.createUrlTree(['/login'], {
      queryParams: { returnUrl: safeAuthReturnUrl(state.url) },
    });
  }

  const requirement = permissionRequirement(route.data);

  if (requirement && authorization.allows(requirement)) {
    return true;
  }

  return router.createUrlTree(['/forbidden'], {
    queryParams: { from: safeAuthReturnUrl(state.url) },
  });
};

/**
 * «الرئيسية» (2026-10-13): for whoever may open one of its cards, each by its
 * own screen's grant -- a requirement no single list of permissions says.
 */
export const homeGuard: CanActivateFn = async (_route, state) => {
  const auth = inject(AuthService);
  const authorization = inject(AuthorizationService);
  const router = inject(Router);

  if (!await auth.restoreSession()) {
    return router.createUrlTree(['/login'], {
      queryParams: { returnUrl: safeAuthReturnUrl(state.url) },
    });
  }

  // A nurse writes herself in there (2026-10-18), with or without a card.
  if (authorization.homeOpen()) {
    return true;
  }

  return router.createUrlTree(['/forbidden'], {
    queryParams: { from: safeAuthReturnUrl(state.url) },
  });
};

export const workspaceLandingRedirect: RedirectFunction = async () => {
  const auth = inject(AuthService);
  const authorization = inject(AuthorizationService);

  return await auth.restoreSession()
    ? authorization.landingRoute()
    : '/login';
};
